import * as React from "react";
import { waitFor, screen } from "@testing-library/react";
import { render, fireEvent } from "./test-utils";
import {
  BasicJewishDate,
  BasicJewishDay,
  BasicJewishDateRange,
  ReactJewishDatePicker,
} from "..";
import {
  getEngJewishMonths,
  dontSelectHolidays,
  dontSelectShabat,
} from "jewish-dates-core";
import { it, expect, describe } from "vitest";

// The dropdown's calendar is only mounted from the first open onward, so tests
// that inspect the grid have to open the picker the way a user would.
const renderOpen = (ui: React.ReactElement) => {
  const result = render(ui);
  fireEvent.click(screen.getByTestId("selectedDate"));
  return result;
};

describe("jewishDatesCore", () => {
  it("change month", async () => {
    const basicJewishDate: BasicJewishDate = {
      day: 13,
      monthName: "Elul",
      year: 5781,
    };

    const { container, getByText } = renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    await waitFor(() => {
      fireEvent.change(screen.getByTestId("month"), {
        target: { value: getEngJewishMonths()[0].id },
      });
    });
    expect(screen.getByTestId("30 Tishri 5781")).toHaveTextContent("30");
  });

  it("change year", async () => {
    const basicJewishDate: BasicJewishDate = {
      day: 13,
      monthName: "Cheshvan",
      year: 5781,
    };
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    await waitFor(() => {
      fireEvent.change(screen.getByTestId("year"), { target: { value: 5780 } });
    });
    expect(screen.getByTestId("30 Cheshvan 5780")).toHaveTextContent("30");
  });

  it("prev month", async () => {
    const basicJewishDate: BasicJewishDate = {
      day: 13,
      monthName: "Cheshvan",
      year: 5781,
    };
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    await waitFor(() => {
      fireEvent.click(screen.getByTestId("prev"));
    });
    expect(screen.getByTestId("30 Tishri 5781")).toHaveTextContent("30");
  });

  it("next month", async () => {
    const basicJewishDate: BasicJewishDate = {
      day: 13,
      monthName: "Cheshvan",
      year: 5781,
    };
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    await waitFor(() => {
      fireEvent.click(screen.getByTestId("next"));
    });
    expect(screen.getByTestId("29 Kislev 5781")).toHaveTextContent("29");
  });

  it("select date", async () => {
    const basicJewishDate: BasicJewishDate = {
      day: 13,
      monthName: "Cheshvan",
      year: 5781,
    };
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    await waitFor(() => {
      fireEvent.click(screen.getByTestId("16 Cheshvan 5781"));
    });
    expect(screen.getByTestId("selectedDate")).toHaveTextContent(
      "16 Cheshvan 5781"
    );
  });

  it("dont select holiday", async () => {
    const excludeHolidays = dontSelectHolidays(true);
    const basicJewishDate: BasicJewishDate = {
      day: 21,
      monthName: "Nisan",
      year: 5782,
    };
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        canSelect={excludeHolidays}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    expect(screen.getByTestId("21 Nisan 5782")).toHaveClass("noSelect");
  });

  it("dont select shabat", async () => {
    const basicJewishDate: BasicJewishDate = {
      day: 20,
      monthName: "Shevat",
      year: 5782,
    };
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        canSelect={dontSelectShabat}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    expect(screen.getByTestId("20 Shevat 5782")).toHaveClass("noSelect");
  });

  it("custom day class", async () => {
    const basicJewishDate: BasicJewishDate = {
      day: 20,
      monthName: "Shevat",
      year: 5782,
    };
    function customizeDayStyle(day: BasicJewishDay): string {
      if (day.date.getDay() === 6) { 
        return "customClass";
      }
      return "";  
    }
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        value={basicJewishDate}
        customizeDayStyle={customizeDayStyle}
        onClick={(day: BasicJewishDay) => {}}
      />
    );

    expect(screen.getByTestId("20 Shevat 5782")).toHaveClass("customClass");
  });

  it("select range", async () => {
    const basicJewishDateRange: BasicJewishDateRange = {
      startDate: {
        day: 16,
        monthName: "Elul",
        year: 5788,
      },
      endDate: {
        day: 20,
        monthName: "Elul",
        year: 5788,
      },
    };
    renderOpen(
      <ReactJewishDatePicker
        isHebrew={false}
        isRange={true}
        value={basicJewishDateRange}
        onClick={(startDay: BasicJewishDay, endDay: BasicJewishDay) => {}}
      />
    );

    await waitFor(() => {
      () => {
        fireEvent.click(screen.getByTestId("16 Elul 5788"));
        fireEvent.click(screen.getByTestId("20 Elul 5788"));
      };
    });
    expect(screen.getByTestId("selectedDate")).toHaveTextContent(
      "16 Elul 5788 - 20 Elul 5788"
    );
  });
});
